import { ActivityEntityName, CustomFieldType, getMetadataEntityFieldType, MetadataEntityFieldData, MetadataEntityFieldType, MetadataEnumTypeData, MetadataFlagTypeData, MetadataLookupTypeData, PickerPriceListRequest, PickerRequest, PickerTagRequest, toPickerTagRequest } from '@jms/model';

/**
 * Shared known-value resolution for an entity field.
 *
 * The filter bar and the kanban column set both need "what values can this field hold" from the same
 * metadata, so the static-option reading and the picker-request shaping live here once. Both consumers
 * describe a field the same way; only what they build from the answer differs.
 */

/** Custom fields carry the generic CustomField type; the real type lives in typeData.type. */
export const CUSTOM_FIELD_TYPE_MAP: Partial<Record<CustomFieldType, MetadataEntityFieldType>> = {
  [CustomFieldType.Text]: MetadataEntityFieldType.String,
  [CustomFieldType.Note]: MetadataEntityFieldType.String,
  [CustomFieldType.Email]: MetadataEntityFieldType.String,
  [CustomFieldType.Phone]: MetadataEntityFieldType.String,
  [CustomFieldType.Number]: MetadataEntityFieldType.Number,
  [CustomFieldType.Date]: MetadataEntityFieldType.Date,
  [CustomFieldType.DateTime]: MetadataEntityFieldType.DateTime,
  [CustomFieldType.Boolean]: MetadataEntityFieldType.Boolean,
  [CustomFieldType.Dropdown]: MetadataEntityFieldType.Enum,
  [CustomFieldType.DropdownMulti]: MetadataEntityFieldType.Enum,
};

/** A known value read straight from metadata — no request needed. */
export type EntityFieldStaticOption = {
  id: string;
  label: string;
  color?: string | null;
  group?: string;
  data?: any;
};

/** Everything needed to ask the picker endpoint for a lookup field's records. */
export type EntityFieldPickerSpec = {
  entity: ActivityEntityName;
  buildRequest: (search?: string) => PickerRequest;
};

/**
 * Resolves the effective first-class field type. Custom fields carry the generic CustomField type;
 * their real type lives in typeData.type — map it so the field gets the correct operators/editor.
 * Unmapped custom-field types (Address/Image) return CustomField, which has no FIELD_TYPE_MAP entry,
 * so they are correctly excluded from the filter dropdown.
 */
export function effectiveEntityFieldType(field: MetadataEntityFieldData): MetadataEntityFieldType {

  const type = getMetadataEntityFieldType(field);
  if (type !== MetadataEntityFieldType.CustomField) return type;

  const customType = (field.typeData as any)?.type as CustomFieldType;
  return CUSTOM_FIELD_TYPE_MAP[customType] ?? MetadataEntityFieldType.CustomField;
}

export function isCustomEntityField(field: MetadataEntityFieldData): boolean {

  return getMetadataEntityFieldType(field) === MetadataEntityFieldType.CustomField;
}

/**
 * Static option list for Enum/Flag fields, or null when the field has no metadata-resident value set.
 *
 * Custom-field dropdowns hold their options in typeData.data as { group, value }[] — the value is both
 * id and label. First-class enums hold MetadataEnumTypeData ({ key, value, color }).
 */
export function resolveEntityFieldStaticOptions(field: MetadataEntityFieldData): EntityFieldStaticOption[] | null {

  const fieldType = effectiveEntityFieldType(field);

  if (fieldType === MetadataEntityFieldType.Enum && isCustomEntityField(field)) {
    const data = (field.typeData as any)?.data as { group?: string; value: string }[];
    return (data ?? []).map(option => ({ id: option.value, label: option.value, group: option.group, data: option.value }));
  }

  if (fieldType === MetadataEntityFieldType.Enum) {
    const data = field.typeData as MetadataEnumTypeData[];
    return (data ?? []).map(option => ({ id: (option as any).value ?? option.key, label: option.key, color: (option as any).color ?? null, data: (option as any).value ?? option.key }));
  }

  if (fieldType === MetadataEntityFieldType.Flag) {
    const data = field.typeData as MetadataFlagTypeData[];
    return (data ?? []).map(option => ({ id: (option as any).value ?? option.key, label: option.key, color: (option as any).color ?? null, data: (option as any).value ?? option.key }));
  }

  return null;
}

/**
 * Picker spec for Lookup/Parent fields, or null when the field is not lookup-shaped.
 *
 * Each branch exists because the target entity's picker needs a discriminator the plain search request
 * does not carry: a status set is chosen by entityType, a recurring template by templateMode, a tag set
 * by its label types, a price list by category.
 */
export function resolveEntityFieldPickerSpec(field: MetadataEntityFieldData, fallbackEntityType?: ActivityEntityName | null): EntityFieldPickerSpec | null {

  const fieldType = effectiveEntityFieldType(field);
  if (fieldType !== MetadataEntityFieldType.Lookup && fieldType !== MetadataEntityFieldType.Parent) return null;

  const lookupData = typeof field.typeData === 'string' ? { type: field.typeData } as MetadataLookupTypeData : field.typeData as MetadataLookupTypeData;
  if (!lookupData?.type) return null;

  const entityOptions = field.entityOptions ?? {};

  if (lookupData.type === ActivityEntityName.EntityStatus) {
    // Status sets are stored per owning entity. typeData.data carries that owner; the board
    // entity is the same fallback entity.header uses when client metadata drops `data`.
    // Spread entityOptions first so an explicit entityType (multi-set fields) still wins,
    // then fill entityType last so a missing/undefined option cannot wipe the status set.
    return {
      entity: lookupData.type,
      buildRequest: (search) => {
        const entityType = entityOptions.entityType ?? lookupData.data ?? fallbackEntityType;
        return { search, ...entityOptions, entityType };
      },
    };
  }

  if (lookupData.type === ActivityEntityName.JobTemplate && lookupData.data === 'Recurring') {
    return { entity: lookupData.type, buildRequest: (search) => ({ templateMode: lookupData.data, search, ...entityOptions }) };
  }

  if (lookupData.type === ActivityEntityName.Tag && lookupData.data) {
    return { entity: lookupData.type, buildRequest: (search) => ({ ...entityOptions, ...toPickerTagRequest(lookupData.data, { search, includeSystemTags: true }) } as PickerTagRequest) };
  }

  if (lookupData.type === ActivityEntityName.PriceList) {
    return { entity: lookupData.type, buildRequest: (search) => ({ category: lookupData.data, includeGlobal: true, search, ...entityOptions } as PickerPriceListRequest) };
  }

  return { entity: lookupData.type, buildRequest: (search) => ({ search, ...entityOptions }) };
}
